/**
 * Letter PDF helpers.
 * PDF is produced ONLY by server-side Word→PDF conversion (LibreOffice or MS Word; no HTML path).
 */

import { letterTemplateService } from "../services/api";

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
      resolve(base64 || "");
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Convert a filled .docx ArrayBuffer to a PDF Blob via LibreOffice on the API server.
 */
export async function convertDocxArrayBufferToPdfBlob(arrayBuffer, { fileName = "letter.docx" } = {}) {
  if (!arrayBuffer) {
    throw new Error("No letter document to convert");
  }
  const docxBase64 = arrayBufferToBase64(arrayBuffer);
  const pdfBlob = await letterTemplateService.convertToPdf({
    docxBase64,
    fileName,
  });
  if (!pdfBlob || !(pdfBlob instanceof Blob)) {
    throw new Error("PDF conversion returned an empty response");
  }
  // API may return JSON error as blob
  if (pdfBlob.type && pdfBlob.type.includes("application/json")) {
    const text = await pdfBlob.text();
    let msg = "Word→PDF conversion failed";
    try {
      msg = JSON.parse(text)?.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (pdfBlob.size < 64) {
    throw new Error("Word→PDF conversion produced an empty file");
  }
  return new Blob([pdfBlob], { type: "application/pdf" });
}

export function collectEmployeeLetterEmails(employee = {}) {
  const personalRaw =
    employee.personalEmail ||
    employee.user?.email ||
    employee.personal_email ||
    "";
  const officialRaw =
    employee.officialEmail ||
    employee.email ||
    employee.official_email ||
    "";
  const normalize = (v) => String(v || "").trim().toLowerCase();
  const isValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const personal = normalize(personalRaw);
  const official = normalize(officialRaw);
  const emails = [];
  if (personal && isValid(personal)) emails.push({ type: "personal", email: personal });
  if (official && isValid(official) && official !== personal) {
    emails.push({ type: "official", email: official });
  } else if (official && isValid(official) && !emails.length) {
    emails.push({ type: "official", email: official });
  }
  return {
    personal: personal && isValid(personal) ? personal : "",
    official: official && isValid(official) ? official : "",
    recipients: emails,
    hasAny: emails.length > 0,
  };
}
